import React, { useCallback, memo } from "react";
import { useTheme } from "../../hooks/useTheme";
import { Portal } from "../Portal/Portal";
import { Toggle } from "../Toggle/Toggle";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { YandexMetrika } from "../../utils/yandexMetrika";
import { useMapFiltersStorage } from "../../hooks/map/useMapFiltersStorage";
import styles from "./MapFilters.module.css";
import dpsIcon from "../icons/markers/light/dps1.png";
import camIcon from "../icons/markers/light/cam.png";
import cleanIcon from "../icons/markers/light/clean1.png";
import dtpIcon from "../icons/markers/light/dtp1.png";

interface MapFiltersProps {
  isOpen: boolean;
  onClose: () => void;
}

const MapFiltersComponent: React.FC<MapFiltersProps> = ({
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const { filters, handlers } = useMapFiltersStorage();

  const handleBack = useCallback(() => {
    HapticFeedback.light();
    onClose();
  }, [onClose]);

  // Мемоизируем все обработчики для предотвращения ре-рендеров Toggle
  const handleTrafficToggle = useCallback(() => {
    HapticFeedback.light();
    const newState = !filters.isTrafficVisible;
    YandexMetrika.toggleTrafficFilter(newState);
    handlers.handleTrafficToggle();
  }, [handlers, filters.isTrafficVisible]);

  const handleDpsToggle = useCallback(() => {
    HapticFeedback.light();
    const newState = !filters.isDpsMarkersVisible;
    YandexMetrika.toggleDpsFilter(newState);
    handlers.handleDpsToggle();
  }, [handlers, filters.isDpsMarkersVisible]);

  const handleWebcamsToggle = useCallback(() => {
    HapticFeedback.light();
    const newState = !filters.isWebcamsMarkersVisible;
    YandexMetrika.toggleWebcamsFilter(newState);
    handlers.handleWebcamsToggle();
  }, [handlers, filters.isWebcamsMarkersVisible]);

  const handleCleanToggle = useCallback(() => {
    HapticFeedback.light();
    const newState = !filters.isCleanMarkersVisible;
    YandexMetrika.toggleCleanFilter(newState);
    handlers.handleCleanToggle();
  }, [handlers, filters.isCleanMarkersVisible]);

  const handleDtpToggle = useCallback(() => {
    HapticFeedback.light();
    const newState = !filters.isDtpMarkersVisible;
    YandexMetrika.toggleDtpFilter(newState);
    handlers.handleDtpToggle();
  }, [handlers, filters.isDtpMarkersVisible]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div className={styles.overlay}>
        <div className={styles.sheetContainer}>
          <button
            className={styles.backButton}
            onClick={(e) => {
              e.stopPropagation();
              handleBack();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className={`${styles.container} ${
              theme === "dark" ? styles.darkTheme : ""
            }`}
            data-theme={theme}
          >
            <div className={styles.content}>
            <div className={styles.itemsWrapper}>
              {/* Трафик */}
              <div className={styles.item}>
                <div className={styles.itemContent}>
                  <div className={styles.leftContent}>
                    <div className={styles.iconWrapper}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_4484_21058)">
                          <path
                            d="M17.25 3H6.75C6.33579 3 6 3.33579 6 3.75V20.25C6 20.6642 6.33579 21 6.75 21H17.25C17.6642 21 18 20.6642 18 20.25V3.75C18 3.33579 17.6642 3 17.25 3Z"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 10.5C13.2426 10.5 14.25 9.49264 14.25 8.25C14.25 7.00736 13.2426 6 12 6C10.7574 6 9.75 7.00736 9.75 8.25C9.75 9.49264 10.7574 10.5 12 10.5Z"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 18C13.2426 18 14.25 16.9926 14.25 15.75C14.25 14.5074 13.2426 13.5 12 13.5C10.7574 13.5 9.75 14.5074 9.75 15.75C9.75 16.9926 10.7574 18 12 18Z"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18 6.75H20.25"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18 14.25H20.25"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3.75 6.75H6"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3.75 14.25H6"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4484_21058">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div className={styles.itemText}>Трафик</div>
                  </div>
                  <Toggle
                    isActive={filters.isTrafficVisible}
                    onClick={handleTrafficToggle}
                    theme={theme}
                  />
                </div>
              </div>

              {/* Посты ДПС */}
              <div className={styles.item}>
                <div className={styles.itemContent}>
                  <div className={styles.leftContent}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={dpsIcon}
                        alt="DPS"
                        className={styles.markerIcon}
                      />
                    </div>
                    <div className={styles.itemText}>Посты ДПС</div>
                  </div>
                  <Toggle
                    isActive={filters.isDpsMarkersVisible}
                    onClick={handleDpsToggle}
                    theme={theme}
                  />
                </div>
              </div>

              {/* Камеры */}
              <div className={styles.item}>
                <div className={styles.itemContent}>
                  <div className={styles.leftContent}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={camIcon}
                        alt="Camera"
                        className={styles.markerIcon}
                      />
                    </div>
                    <div className={styles.itemText}>Камеры</div>
                  </div>
                  <Toggle
                    isActive={filters.isWebcamsMarkersVisible}
                    onClick={handleWebcamsToggle}
                    theme={theme}
                  />
                </div>
              </div>

              {/* Чисто */}
              <div className={styles.item}>
                <div className={styles.itemContent}>
                  <div className={styles.leftContent}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={cleanIcon}
                        alt="Clean"
                        className={styles.markerIcon}
                      />
                    </div>
                    <div className={styles.itemText}>Чисто</div>
                  </div>
                  <Toggle
                    isActive={filters.isCleanMarkersVisible}
                    onClick={handleCleanToggle}
                    theme={theme}
                  />
                </div>
              </div>

              {/* ДТП */}
              <div className={styles.item}>
                <div className={styles.itemContent}>
                  <div className={styles.leftContent}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={dtpIcon}
                        alt="DTP"
                        className={styles.markerIcon}
                      />
                    </div>
                    <div className={styles.itemText}>ДТП</div>
                  </div>
                  <Toggle
                    isActive={filters.isDtpMarkersVisible}
                    onClick={handleDtpToggle}
                    theme={theme}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
};

// Мемоизируем компонент для предотвращения лишних ре-рендеров
export const MapFilters = memo(MapFiltersComponent);
